import React, { FC, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Content } from 'antd/lib/layout/layout';
import { Card, Row, Button, Typography, Col } from 'antd';
import {
	changeFilterValueToArrayOfIds,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import {
	budgetsRestCrudThunksForBuyer,
	budgetsRestCrudThunksForSupplier,
} from '../../thunks/budget_thunks';
import { BUDGETS_CRUD_ACTION_CREATORS } from '../../actions/budget_actions';
import { EmptyState } from '../empty_state/EmptyState';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import moment from 'moment';
import { dateFormatter } from '../../utils/DataFormatterUtils';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import SubnavBar from '../subnav_bar/SubnavBar';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import { Form } from '@ant-design/compatible';
import { DownloadOutlined } from '@ant-design/icons';
import LocationsTreeSelect from '../locations_tree_select/LocationsTreeSelect';
import { PLANNED_MAINTENANCE_SCHEDULE_GROUPS_CRUD_ACTION_CREATORS } from '../../actions/planned_maintenance_schedule_groups_actions';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import { INITIAL_ACTIVE_FILTERS, IS_ACTIVE_FILTER_ITEMS } from '../../utils/FilterUtils';
import queryString from 'qs';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';

const BUDGET_TARGET_COLLECTION = 'budgetsIndex';
const { Text } = Typography;

const BudgetIndexPage: FC<any> = ({
	history,
	location,
	updateBudgetFilters,
	clearAndUpdateFilters,
	budget,
	budgetSlice,
	currentUser,
	userType,

	createBudget,
}): React.ReactElement => {
	const [showModal, setShowModal] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);

	const style = require('../../styles/rowDisplay.less');

	const currency = getCurrency({ currentUser });
	useMemo(() => {
		updateBudgetFilters({}, BUDGET_TARGET_COLLECTION);
	}, []);

	const initialFilters = useMemo(() => {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		const {
			current,
			pageSize,
			total,
			pageSizeOptions,
			showSizeChanger,
			onShowSizeChange,
			sort_by,
			order,
			...budgetFilters
		} = queryParams;

		updateBudgetFilters(budgetFilters || {}, BUDGET_TARGET_COLLECTION);
		history.push(`/${userType}/workflows/budgets/overview?${new URLSearchParams(location.search)}`);
		return budgetFilters;
	}, []);

	const handleSubmit = (values) => {
		let budget = {
			id: values.id,
		};
		setButtonLoading(true);
		const submitRule = createBudget;
		setButtonLoading(true);
		submitRule(budget)
			.then(() => {
				setButtonLoading(false);
				setShowModal(false);
			})
			.catch((e) => {
				console.log(e);
				console.log('error');
			});
	};
	const budgetThresholdPill = (remaining, budget, consumed, threshold) => {
		if (remaining < 0) {
			return (
				<span
					style={{
						color: 'rgb(163, 40, 40)',
						backgroundColor: 'rgb(249, 230, 230)',
						padding: '4px 12px',
						borderRadius: 16,
					}}
				>
					{(Math.abs(remaining) * 100).toFixed(0)}% Over Budget
				</span>
			);
		} else if (consumed > threshold) {
			return (
				<span
					style={{
						color: 'rgb(179, 98, 30)',
						backgroundColor: 'rgb(251, 239, 229)',
						padding: '4px 12px',
						borderRadius: 16,
					}}
				>
					{(remaining * 100).toFixed(0)}% Remaining
				</span>
			);
		} else {
			return <span className="rowGrayChip">{(remaining * 100).toFixed(0)}% Remaining</span>;
		}
	};
	const columns = [
		{
			title: 'title',
			dataIndex: 'title',
			defaultSortOrder: 'ascend',
			render: (text, record) => {
				const totalBudget =
					record.totalBudgetForCurrentCycle !== undefined
						? record.totalBudgetForCurrentCycle
						: record.sharedBudget
						? record.sharedBudget
						: 0;

				let budget = {
					budget: totalBudget,
					budgetAccrued: record.budgetPerLocations ? 0 : parseFloat(record.budgetAccrued),
					budgetConsumed: record.budgetPerLocations ? 0 : parseFloat(record.budgetConsumed),
					budgetSpent: record.budgetPerLocations ? 0 : parseFloat(record.budgetSpent),
				};

				if (record.totalBudgetForCurrentCycle === undefined && record.budgetPerLocations) {
					record.budgetPerLocations.map((item) => {
						if (item['budget']) {
							budget['budget'] = budget['budget'] + parseFloat(item['budget']);
						}
						budget['budgetAccrued'] = budget['budgetAccrued'] + parseFloat(item['budgetAccrued']);
						budget['budgetConsumed'] =
							budget['budgetConsumed'] + parseFloat(item['budgetConsumed']);
						budget['budgetSpent'] = budget['budgetSpent'] + parseFloat(item['budgetSpent']);
					});
				}

				const remainingAmount =
					record.remainingBudgetForCurrentCycle !== undefined
						? record.remainingBudgetForCurrentCycle
						: budget.budget - budget.budgetConsumed;

				const remaining = budget.budget > 0 ? remainingAmount / budget.budget : null;
				const isActive =
					(record.endDate
						? moment(record.endDate).startOf('day').valueOf() >= moment().startOf('day').valueOf()
						: true) && // no end date
					moment(record.startDate).startOf('day').valueOf() <= moment().startOf('day').valueOf();
				return (
					<Row>
						<Col className={'flex flex-grow'} span={16}>
							<Row className={'rowColumn'}>
								<div className="rowPrimary w-full">
									<Ellipsis
										tooltip={true}
										fullWidthRecognition={true}
										lines={1}
										style={{
											color: 'rgba(0,0,0,0.65)',
											paddingBottom: '1px',
											marginRight: '5%',
											overflow: 'hidden',
										}}
									>
										{record.title}
									</Ellipsis>
								</div>
								<div className="rowSecondary w-full gap-y-4">
									{isActive ? null : (
										<div>
											<Text type={'danger'}>Inactive</Text>
										</div>
									)}
									{remaining && !Number.isNaN(remaining) ? (
										<div className="rowInlineGroup">
											{budgetThresholdPill(
												remaining,
												budget.budget,
												budget.budgetConsumed,
												(parseFloat(record.thresholdPercentage) / 100) * budget.budget
											)}
										</div>
									) : null}
									{isActive ? (
										<div className="rowInlineGroup">
											<span className="rowGrayChip">
												Current Period: {dateFormatter(record.cycleStartDate, 'MMM D, YYYY')} -{' '}
												{dateFormatter(record.cycleEndDate, 'MMM D, YYYY')}
											</span>
										</div>
									) : null}
									<div className="rowInlineGroup">
										<span className="rowGrayChip">
											Created {moment(record.createdAt).fromNow()}
										</span>
									</div>
								</div>
							</Row>
						</Col>
						<Col span={8}>
							<div className={'flex flex-col items-end gap-y-4 font-normal text-grey-7'}>
								<span>{currency.format(budget.budget)} Budget Total</span>
								{remainingAmount > 0 ? (
									<span>{currency.format(remainingAmount)} Remaining</span>
								) : null}
							</div>
						</Col>
					</Row>
				);
			},
		},
	];
	const onRow = (record) => ({
		onClick: () => {
			let searchString = location.search;
			if (searchString[0] === '?') {
				searchString = searchString.slice(1);
			}
			let backUrl = `/${userType}/budgets/detail/${record.id}/details`;
			if (searchString && searchString.length > 0) {
				backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
			}
			history.push(backUrl);
		},
	});

	const filterConfig = [
		{
			fieldName: 'isActive',
			type: FILTER_FIELD_TYPE.RADIO_GROUP,
			items: IS_ACTIVE_FILTER_ITEMS(false),
			filtersValueAccessor: (filtersStateSlice) =>
				nullSafeGet(`${BUDGET_TARGET_COLLECTION}.filters.isActive`, filtersStateSlice),
			defaultValue: undefined,
			keyAccessor: (item) => item.value,
			valueAccessor: (item) => item.value,
			handleChange: (isActive) => {
				updateBudgetFilters({ isActive }, BUDGET_TARGET_COLLECTION);
			},
			renderRecord: (item) => item.label,
			component: OWRadioGroup,
			showOutsideDropdown: true,
		},
	];

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<SubnavBar
						left={
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<AdvancedFilters
									updateFilters={updateBudgetFilters}
									filterConfig={filterConfig}
									filtersTargetCollectionName={BUDGET_TARGET_COLLECTION}
									clearAndUpdateFilters={clearAndUpdateFilters}
									filtersStateSlice={budgetSlice}
									preAppliedFilters={{}}
									initialFilters={{ ...initialFilters, isActive: true }}
									inputParamName={'search'}
								/>
							</div>
						}
						right={
							<div className="flex flex-row items-center">
								<AccessPermissionChecker name={PERMISSION_NAMES.ADD_BUDGET}>
									<Button type="primary" onClick={() => history.push(`/${userType}/budgets/new`)}>
										Create Budget
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				</Col>
			</Row>
			<div className="mb-3 flex w-full flex-1 flex-row">
				<SelectedFilters
					updateFilters={(filters) => {
						updateBudgetFilters(filters, BUDGET_TARGET_COLLECTION);
					}}
					filterValues={nullSafeGetOrElse(`${BUDGET_TARGET_COLLECTION}.filters`, budgetSlice, {})}
					filterConfig={filterConfig}
				/>
			</div>
			<Row className="rowSpacing">
				<Card className={'w-full'}>
					<PaginatedReduxTable
						updateQueryParams={true}
						emptyState={
							<EmptyState
								graphic={
									<img
										alt="Currently there are no assets!"
										style={{ marginBottom: 8 }}
										src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
									/>
								}
								headline={"It's a little lonely in here."}
								body={
									<div style={{ textAlign: 'center' }}>
										<div style={{ maxWidth: 440, marginBottom: 16 }}>
											Looks like your team has not added any budgets yet.
										</div>
										<AccessPermissionChecker name={PERMISSION_NAMES.ADD_BUDGET}>
											<Button
												type="primary"
												onClick={() => history.push(`/${userType}/budgets/new`)}
											>
												Add your first budget
											</Button>
										</AccessPermissionChecker>
									</div>
								}
							/>
						}
						collectionName="budgets"
						onRow={onRow}
						showHeader={false}
						targetCollectionName={BUDGET_TARGET_COLLECTION}
						columns={columns}
						keyAccessor={(el) => el.id}
						initialFilters={{ ...initialFilters, isActive: 'true' }}
						initialPagination={{}}
						initialSorting={{ sort_by: 'createdAt', order: 'desc' }}
						hiddenPagination={{ showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
						fetchData={
							userType == ROLE_TYPES.SUPPLIER
								? budgetsRestCrudThunksForSupplier.read
								: budgetsRestCrudThunksForBuyer.read
						}
					/>
				</Card>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	budgetSlice: state.budgets,
	budget: state.budgets.detail,
	companySettings: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	updateBudgetFilters: (filters, targetCollection) =>
		dispatch(BUDGETS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(BUDGETS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(BudgetIndexPage as any)) as any
);
